<template>
  <div class="cl-table-columns">
    <div class="cl-table-columns__header">
      {{ $t('LABEL_TABLE_COLUMNS') }}
    </div>

    <draggable
      v-model="columns"
      handle=".cl-table-columns__drag-handle"
      @end="onReorder"
    >
      <div
        v-for="col in columns"
        :key="col.uid"
        class="cl-table-columns__col"
      >
        <div class="cl-table-columns__row">
          <span class="cl-table-columns__drag-handle">≡</span>

          <input
            v-model="col.name"
            :placeholder="$t('LABEL_COLUMN_NAME_PLACEHOLDER')"
            class="cl-table-columns__name"
            @change="onFieldChange(col, 'name')"
          />

          <select
            v-model="col.column_type"
            class="cl-table-columns__type"
            @change="onFieldChange(col, 'column_type')"
          >
            <option value="text">{{ $t('LABEL_COLUMN_TYPE_TEXT') }}</option>
            <option value="number">{{ $t('LABEL_COLUMN_TYPE_NUMBER') }}</option>
            <option value="select">{{ $t('LABEL_COLUMN_TYPE_SELECT') }}</option>
          </select>

          <label class="cl-table-columns__required">
            <input
              type="checkbox"
              v-model="col.required"
              @change="onFieldChange(col, 'required')"
            />
            {{ $t('LABEL_COLUMN_REQUIRED') }}
          </label>

          <button
            type="button"
            class="cl-table-columns__cond-format"
            @click="openCondFormatModal(col)"
            :title="$t('COND_FORMAT_SECTION')"
            aria-label="Conditional formatting"
          >
            <span :class="['cl-table-columns__cond-format-dot', col.condFormat && col.condFormat.length > 0 ? 'has-rules' : '']">●</span>
          </button>

          <button
            type="button"
            class="cl-table-columns__delete"
            @click="deleteColumn(col)"
            aria-label="Delete column"
          >
            ×
          </button>
        </div>

        <div
          v-if="col.column_type === 'select'"
          class="cl-table-columns__options"
        >
          <div class="cl-table-columns__options-header">
            {{ $t('LABEL_COLUMN_OPTIONS') }}
          </div>
          <div
            v-for="opt in (optionsByColumn[col.uid] || [])"
            :key="opt.uid"
            class="cl-table-columns__option-row"
          >
            <input
              v-model="opt.name"
              :placeholder="$t('LABEL_OPTION_NAME_PLACEHOLDER')"
              class="cl-table-columns__option-name"
              @change="onOptionChange(col, opt)"
            />
            <button
              type="button"
              class="cl-table-columns__option-delete"
              @click="deleteOption(col, opt)"
              aria-label="Delete option"
            >
              ×
            </button>
          </div>
          <button
            type="button"
            class="cl-table-columns__option-add"
            @click="addOption(col)"
          >
            + {{ $t('LABEL_ADD_OPTION') }}
          </button>
        </div>
      </div>
    </draggable>

    <button
      type="button"
      class="cl-table-columns__add"
      @click="addColumn"
    >
      + {{ $t('LABEL_ADD_COLUMN') }}
    </button>

    <ColumnCondFormatModal
      :show="condFormatModalShow"
      :column="condFormatModalColumn || {}"
      :value="condFormatModalRules"
      @confirm="onCondFormatConfirm"
      @cancel="onCondFormatCancel"
      @update:show="(v) => { if (!v) onCondFormatCancel(); }"
    />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import {
  getTableColumns,
  createItem,
  updateItem,
  deleteItem,
  getColumnOptions,
} from "@/services/checklist.service";
import ColumnCondFormatModal from "./ColumnCondFormatModal.vue";

export default {
  name: "ClTableColumns",
  components: { draggable, ColumnCondFormatModal },
  props: {
    clUid:       { type: String, required: true },
    questionUid: { type: String, required: true },
  },
  data() {
    return {
      columns: [],
      optionsByColumn: {},   // { [columnUid]: [{uid, name, ...}] }
      condFormatModalShow: false,
      condFormatModalColumn: null,
      condFormatModalRules: [],
    };
  },
  mounted() {
    this.loadColumns();
  },
  watch: {
    questionUid() { this.loadColumns(); },
  },
  methods: {
    async loadColumns() {
      const res = await getTableColumns(this.clUid, this.questionUid);
      this.columns = (res && res.data) || [];
      // Eager-load options for select-type columns
      for (const col of this.columns) {
        if (col.column_type === "select") {
          this.loadOptions(col);
        }
      }
    },

    async addColumn() {
      const res = await createItem({
        type: "tableColumn",
        cl_uid: this.clUid,
        question_uid: this.questionUid,
        name: "",
        name_en: "",
      });
      const newCol = res && res.data;
      if (newCol) this.columns.push(newCol);
    },

    async onFieldChange(col, field) {
      await updateItem({
        type: "tableColumn",
        uid: col.uid,
        fields: { [field]: col[field] },
      });
      if (field === "column_type" && col.column_type === "select") {
        this.loadOptions(col);
      }
    },

    async onReorder() {
      await Promise.all(
        this.columns.map((col, idx) =>
          updateItem({
            type: "tableColumn",
            uid: col.uid,
            fields: { pos: idx + 1 },
          })
        )
      );
      this.columns.forEach((col, idx) => { col.pos = idx + 1; });
    },

    async deleteColumn(col) {
      if (!confirm(`Spalte "${col.name || '(ohne Name)'}" wirklich löschen?`)) {
        return;
      }
      await deleteItem({
        type: "tableColumn",
        uid: col.uid,
      });
      this.columns = this.columns.filter(c => c.uid !== col.uid);
    },

    async loadOptions(col) {
      if (col.column_type !== "select") return;
      const res = await getColumnOptions(this.clUid, col.uid);
      this.$set(this.optionsByColumn, col.uid, (res && res.data) || []);
    },

    async addOption(col) {
      const res = await createItem({
        type: "columnOption",
        cl_uid: this.clUid,
        question_uid: this.questionUid,
        column_uid: col.uid,
        name: "",
        name_en: "",
      });
      // Backend returns { list, entry_uid } — use the refreshed list
      const list = res && res.data && res.data.list;
      if (list) this.$set(this.optionsByColumn, col.uid, list);
    },

    async onOptionChange(col, option) {
      await updateItem({
        type: "columnOption",
        cl_uid: this.clUid,
        column_uid: col.uid,
        uid: option.uid,
        fields: { name: option.name },
      });
    },

    async deleteOption(col, option) {
      if (!confirm(`Wert "${option.name || '(ohne Name)'}" wirklich löschen?`)) {
        return;
      }
      const res = await deleteItem({
        type: "columnOption",
        cl_uid: this.clUid,
        column_uid: col.uid,
        uid: option.uid,
      });
      const list = (res && res.data) || [];
      this.$set(this.optionsByColumn, col.uid, list);
    },

    openCondFormatModal(col) {
      this.condFormatModalColumn = col;
      this.condFormatModalRules = Array.isArray(col.condFormat) ? [...col.condFormat] : [];
      this.condFormatModalShow = true;
    },
    async onCondFormatConfirm(newRules) {
      if (!this.condFormatModalColumn) return;
      const col = this.condFormatModalColumn;
      await updateItem({
        type: "tableColumn",
        uid: col.uid,
        fields: { condFormat: newRules },
      });
      // Update local state so re-opening the modal shows the new rules
      this.$set(col, "condFormat", newRules);
      this.condFormatModalShow = false;
      this.condFormatModalColumn = null;
    },
    onCondFormatCancel() {
      this.condFormatModalShow = false;
      this.condFormatModalColumn = null;
    },
  },
};
</script>

<style scoped>
.cl-table-columns {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 12px;
  margin-top: 12px;
}
.cl-table-columns__header {
  font-weight: bold;
  margin-bottom: 8px;
}
.cl-table-columns__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}
.cl-table-columns__drag-handle {
  cursor: grab;
  color: #888;
  padding: 0 4px;
  user-select: none;
}
.cl-table-columns__name {
  flex: 1;
  padding: 4px 6px;
}
.cl-table-columns__type {
  width: 100px;
}
.cl-table-columns__required {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
}
.cl-table-columns__delete {
  background: none;
  border: none;
  color: #c00;
  font-size: 18px;
  cursor: pointer;
  padding: 0 6px;
}
.cl-table-columns__add {
  margin-top: 8px;
  padding: 6px 12px;
  background: #f0f0f0;
  border: 1px dashed #aaa;
  border-radius: 4px;
  cursor: pointer;
}
.cl-table-columns__col {
  border-bottom: 1px solid #f0f0f0;
}
.cl-table-columns__row {
  border-bottom: none;   /* moved to __col */
}
.cl-table-columns__options {
  margin-left: 30px;
  padding: 4px 0 8px 0;
  background: #fafafa;
  border-left: 2px solid #ddd;
  padding-left: 8px;
}
.cl-table-columns__options-header {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}
.cl-table-columns__option-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
}
.cl-table-columns__option-name {
  flex: 1;
  padding: 3px 6px;
  font-size: 13px;
}
.cl-table-columns__option-delete {
  background: none;
  border: none;
  color: #c00;
  font-size: 16px;
  cursor: pointer;
  padding: 0 6px;
}
.cl-table-columns__option-add {
  margin-top: 4px;
  padding: 4px 10px;
  background: #f0f0f0;
  border: 1px dashed #aaa;
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
}
.cl-table-columns__cond-format {
  background: none;
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 0 6px;
  color: #888;
}
.cl-table-columns__cond-format-dot {
  font-size: 14px;
  color: #ccc;
}
.cl-table-columns__cond-format-dot.has-rules {
  color: #ff9800;
}
</style>
